import { Link, useMatches } from "@tanstack/react-router";

import { writePref } from "../prefs";
import { useNavBrands } from "../shell/nav-links";
import { frameOf } from "./frame";
import { type Place, type Ui, whereIs } from "./places";
import classes from "./ui-switch.module.css";

interface Common {
  className: string | undefined;
  "aria-current": "page" | undefined;
  onClick: () => void;
  /** A Link marks itself current when the page is under its target; only its own page counts. */
  activeOptions: { exact: true };
}

/** Classic's page for a place, else Classic's home. */
function ClassicLink(props: { place: Place | undefined; teamId: string; brandId: string | undefined; common: Common }) {
  const { place, teamId, brandId, common } = props;
  const team = { teamId };
  if (brandId !== undefined) {
    const brand = { teamId, brandId };
    switch (place) {
      case "editor":
        return <Link to="/teams/$teamId/brands/$brandId/editor" params={brand} {...common}>Classic</Link>;
      case "image":
        return <Link to="/teams/$teamId/brands/$brandId/generate" params={brand} {...common}>Classic</Link>;
      case "video":
        return (
          <Link to="/teams/$teamId/brands/$brandId/generate" params={brand} search={{ medium: "video" }} {...common}>
            Classic
          </Link>
        );
      case "sound":
        return <Link to="/teams/$teamId/brands/$brandId/sounds" params={brand} {...common}>Classic</Link>;
      case "actors":
        return <Link to="/teams/$teamId/brands/$brandId/actors" params={brand} {...common}>Classic</Link>;
      case "templates":
        return <Link to="/teams/$teamId/brands/$brandId/templates" params={brand} {...common}>Classic</Link>;
      case "library":
        return <Link to="/teams/$teamId/brands/$brandId/library" params={brand} {...common}>Classic</Link>;
      case "products":
        return <Link to="/teams/$teamId/brands/$brandId/products" params={brand} {...common}>Classic</Link>;
      default:
        break;
    }
  }
  switch (place) {
    case "generations":
      return <Link to="/teams/$teamId/jobs" params={team} {...common}>Classic</Link>;
    case "brands":
      return <Link to="/teams/$teamId/brands" params={team} {...common}>Classic</Link>;
    case "settings":
      return <Link to="/teams/$teamId/members" params={team} {...common}>Classic</Link>;
    case "account":
      return <Link to="/teams/$teamId/account" params={team} {...common}>Classic</Link>;
    case "appearance":
      return <Link to="/teams/$teamId/account/appearance" params={team} {...common}>Classic</Link>;
    case "achievements":
      return <Link to="/teams/$teamId/account/achievements" params={team} {...common}>Classic</Link>;
    case "tokens":
      return <Link to="/teams/$teamId/tokens" params={team} {...common}>Classic</Link>;
    case "newTeam":
      return <Link to="/teams/new" {...common}>Classic</Link>;
    default:
      return <Link to="/teams/$teamId" params={team} {...common}>Classic</Link>;
  }
}

/** Pro's page for a place under the brand, else Pro's home; with no brand, the team's way in. */
function ProLink(props: { place: Place | undefined; teamId: string; brandId: string | undefined; common: Common }) {
  const { place, teamId, brandId, common } = props;
  if (brandId === undefined) {
    return <Link to="/teams/$teamId/pro" params={{ teamId }} {...common}>Pro</Link>;
  }
  const brand = { teamId, brandId };
  switch (place) {
    case "image":
      return <Link to="/teams/$teamId/brands/$brandId/pro/create-image" params={brand} {...common}>Pro</Link>;
    case "video":
      return <Link to="/teams/$teamId/brands/$brandId/pro/create-video" params={brand} {...common}>Pro</Link>;
    case "sound":
      return <Link to="/teams/$teamId/brands/$brandId/pro/create-sound" params={brand} {...common}>Pro</Link>;
    case "actors":
      return <Link to="/teams/$teamId/brands/$brandId/pro/actors" params={brand} {...common}>Pro</Link>;
    case "generations":
      return <Link to="/teams/$teamId/brands/$brandId/pro/generations" params={brand} {...common}>Pro</Link>;
    case "templates":
      return <Link to="/teams/$teamId/brands/$brandId/pro/templates" params={brand} {...common}>Pro</Link>;
    case "library":
      return <Link to="/teams/$teamId/brands/$brandId/pro/library" params={brand} {...common}>Pro</Link>;
    case "products":
      return <Link to="/teams/$teamId/brands/$brandId/pro/products" params={brand} {...common}>Pro</Link>;
    case "brands":
      return <Link to="/teams/$teamId/brands/$brandId/pro/brands" params={brand} {...common}>Pro</Link>;
    case "settings":
      return <Link to="/teams/$teamId/brands/$brandId/pro/settings" params={brand} {...common}>Pro</Link>;
    case "account":
      return <Link to="/teams/$teamId/brands/$brandId/pro/settings/account" params={brand} {...common}>Pro</Link>;
    case "appearance":
      return <Link to="/teams/$teamId/brands/$brandId/pro/settings/appearance" params={brand} {...common}>Pro</Link>;
    case "achievements":
      return <Link to="/teams/$teamId/brands/$brandId/pro/settings/achievements" params={brand} {...common}>Pro</Link>;
    case "tokens":
      return <Link to="/teams/$teamId/brands/$brandId/pro/settings/tokens" params={brand} {...common}>Pro</Link>;
    case "newTeam":
      return <Link to="/teams/$teamId/brands/$brandId/pro/teams/new" params={brand} {...common}>Pro</Link>;
    case "editor":
      return <Link to="/teams/$teamId/brands/$brandId/pro/editor" params={brand} {...common}>Pro</Link>;
    default:
      return <Link to="/teams/$teamId/brands/$brandId/pro" params={brand} {...common}>Pro</Link>;
  }
}

/**
 * A staff page is the same page in every UI: each button opens it again, its search kept, in its
 * UI's frame, which `?ui=` names and Classic's drops (spec ui-perf/admin-panel-pro-simple).
 */
function StaffPill({ common }: { common: (ui: Ui) => Common }) {
  return (
    <nav className={classes.pill} aria-label="Classic, Simple or Pro">
      <Link to="." search={(prev) => ({ ...prev, ui: undefined })} {...common("classic")}>
        Classic
      </Link>
      <Link to="." search={(prev) => ({ ...prev, ui: "simple" })} {...common("simple")}>
        Simple
      </Link>
      <Link to="." search={(prev) => ({ ...prev, ui: "pro" })} {...common("pro")}>
        Pro
      </Link>
    </nav>
  );
}

/**
 * Classic, Simple and Pro: one pill in both headers, the current UI pressed. A button opens the
 * same place in its UI where both have it, else its home, and remembers the choice in this browser,
 * which the app's landing then opens (spec ui-perf/ui-switch-classic-simple-pro).
 */
export function UiSwitch({ teamId, onNavigate }: {
  teamId: string;
  /** Called on a click, as a menu that holds the pill closes then (the account menu's, a phone). */
  onNavigate?: (() => void) | undefined;
}) {
  const here = useMatches({
    select: (matches) => {
      const leaf = matches.at(-1);
      const medium = (leaf?.search as { medium?: unknown } | undefined)?.medium;
      return whereIs(
        leaf?.routeId ?? "",
        typeof medium === "string" ? medium : undefined,
        frameOf(leaf?.search),
      );
    },
  });
  // The page's brand, else the team's first, as Classic's brand select shows it.
  const brandId = useNavBrands(teamId).linkBrandId;
  const common = (ui: Ui): Common => ({
    className: classes.ui,
    "aria-current": here.ui === ui ? "page" : undefined,
    onClick: () => {
      writePref("ui", ui);
      onNavigate?.();
    },
    activeOptions: { exact: true },
  });
  if (here.place === "staff") return <StaffPill common={common} />;
  return (
    <nav className={classes.pill} aria-label="Classic, Simple or Pro">
      <ClassicLink place={here.place} teamId={teamId} brandId={brandId} common={common("classic")} />
      {brandId === undefined ? (
        <Link to="/teams/$teamId/pro" params={{ teamId }} {...common("simple")}>
          Simple
        </Link>
      ) : (
        <Link to="/teams/$teamId/brands/$brandId/simple" params={{ teamId, brandId }} {...common("simple")}>
          Simple
        </Link>
      )}
      <ProLink place={here.place} teamId={teamId} brandId={brandId} common={common("pro")} />
    </nav>
  );
}
